<template>
  <div>
    <div
      v-if="label"
      class="label admin-select-label d-flex align-items-center"
    >
      {{ label }}
      <span v-if="tooltipText !== ''">
        <Tooltip :placement="tooltipPlacement">
          <template #trigger>
            <i class="fas fa-info-circle ml-1"></i>
          </template>
          <template #tooltip>
            <span>{{ tooltipText }}</span>
          </template>
        </Tooltip>
      </span>
    </div>
    <div ref="selectBox" class="admin-select-box" :class="{ open, disabled }">
      <div
        :class="[
          'select-content d-flex align-items-center justify-content-between full-width position-relative',
          { 'select-error': findByName(validationErrors, name) }
        ]"
        tabindex="0"
        @click="toggleOpen"
      >
        <div
          class="d-flex flex-row no-wrap"
          :class="selectedValueClass"
          :style="maxOptionWidth ? `width: ${maxOptionWidth}px` : ''"
        >
          <slot name="selectedValue">
            <span
              >{{ selectedLabel
              }}<span
                v-if="selectedOption && selectedOption.icon"
                class="option-icon"
                ><i :class="selectedOption.icon"></i></span
            ></span>
            <span v-if="selectedHintLabel" class="hint-label">{{
              ` (${selectedHintLabel})`
            }}</span>
          </slot>
        </div>
        <i class="fa fa-chevron-down down-icon" />
        <Transition>
          <div v-if="open" ref="options" class="admin-select-options bg-white">
            <div v-if="hasAddItemListener" class="add-item-input">
              <TextField
                ref="addInput"
                class="full-width mb-0"
                placeholder="Enter new item"
                @keypress="handleEnter"
                @click.stop
              />
              <div
                class="add-item-button"
                data-cy="add-item-button"
                tabindex="0"
                @click="handleAdd"
              >
                <i class="fas fa-plus-circle mr-1"></i>
                Add
              </div>
            </div>
            <div
              v-for="option in selectOptions"
              :key="option.value"
              class="option"
              :class="{ selected: option.value === selectedValue }"
              @click="handleSelect(option)"
            >
              <span>{{ option.label }}</span>
              <span v-if="option.icon" class="option-icon"
                ><i :class="option.icon"></i
              ></span>
              <span v-if="option.hintLabel" class="hint-label">{{
                ` (${option.hintLabel})`
              }}</span>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    <span
      v-if="findByName(validationErrors, name)"
      class="text-danger error-text"
    >
      {{ findByName(validationErrors, name) }}
    </span>
  </div>
</template>

<script>
import { isFalsy } from "@/lib/helpers.js";
import { findByName } from "@/lib/vuelidate_helpers";

import TextField from "@/components/TextField.vue";
import Tooltip from "@/components/Tooltip.vue";
import Transition from "./Transition.vue";

export default {
  name: "SelectBox",
  components: {
    TextField,
    Tooltip,
    Transition
  },
  props: {
    modelValue: { type: [Number, String], default: "" },
    name: { type: String, default: "Value" },
    allowEmpty: {
      type: Boolean,
      default: () => true
    },
    placeholder: {
      type: String,
      default: "Select..."
    },
    options: {
      type: Array,
      default: () => []
    },
    validationErrors: {
      type: Object,
      default() {
        return {
          first: () => {}
        };
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    tooltipText: {
      type: String,
      default: ""
    },
    tooltipPlacement: {
      type: String,
      default: "top"
    },

    // For situations where we don't want to resize the select based on the largest text
    noAdjustedOptionWidth: {
      type: Boolean,
      default: false
    },
    onAddItem: {
      type: Function
    }
  },
  emits: ["update:modelValue", "blur", "add-item"],
  data() {
    return {
      open: false,
      maxOptionWidth: 0,
      selectedValue: null
    };
  },
  computed: {
    hasAddItemListener() {
      return this.$props.onAddItem;
    },
    selectedOption() {
      return this.options.find((opt) => opt.value === this.selectedValue);
    },
    selectedLabel() {
      if (isFalsy(this.selectedValue)) {
        return this.placeholder;
      }
      return this.selectedOption ? this.selectedOption.label : this.placeholder;
    },
    selectedHintLabel() {
      return this.selectedOption ? this.selectedOption.hintLabel : null;
    },
    selectOptions() {
      const options = [...this.options];
      if (this.allowEmpty)
        options.unshift({ value: null, label: this.placeholder });
      return options;
    },
    selectedValueClass() {
      return this.selectedHintLabel ? "selected-value-hint" : "selected-value";
    }
  },
  watch: {
    selectedValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit("update:modelValue", newValue);
        this.$emit("blur", newValue);
      }
    },
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.selectedValue = newValue;
      }
    },
    options(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.calculateWidth();
      }
    },
    open(newValue) {
      if (newValue) {
        document.addEventListener("keypress", this.handleSearch);
      } else {
        document.removeEventListener("keypress", this.handleSearch);
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOut);
    if (!isFalsy(this.modelValue)) {
      this.selectedValue = this.modelValue;
    }
    this.calculateWidth();
    console.warn(
      "SelectBox is deprecated. Please use Select from serviceshift-ui."
    );
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOut);
  },
  methods: {
    calculateWidth() {
      if ((this.options.length || this.placeholder) && !this.fullWidth) {
        const getLabelLength = (option) => {
          let label = option.label || "";
          if (option.hintLabel) {
            label += ` (${option.hintLabel})`;
          }
          return label.length;
        };
        const longestOption = [...this.selectOptions].sort((a, b) =>
          getLabelLength(a) > getLabelLength(b) ? -1 : 0
        )[0];
        const mycanvas = document.createElement("canvas");
        const context = mycanvas.getContext("2d");
        context.font = "14px Source Sans Pro";
        let innerText = longestOption.label;
        if (longestOption.hintLabel) {
          innerText += ` (${longestOption.hintLabel})`;
        }
        const padding = 5;
        const defaultWidth = this.$refs.selectBox.offsetWidth - 40;
        const calculatedWidth = context.measureText(innerText).width + padding;
        // Either full width of select input or larger if it needs to expand
        if (this.noAdjustedOptionWidth) {
          this.maxOptionWidth = defaultWidth;
        } else {
          const maxWidth = Math.max(defaultWidth, calculatedWidth);
          this.maxOptionWidth = maxWidth;
        }
      }
    },
    handleAdd() {
      const value = this.$refs.addInput.$refs.input.value;
      const option = this.options.find((opt) => opt.value === value);
      if (!option && value.trim()) {
        this.$emit("add-item", this.$refs.addInput.$refs.input.value);
        this.toggleOpen();
      } else {
        this.handleSelect(option);
      }
    },
    handleEnter(e) {
      if (e.key === "Enter") {
        this.handleAdd();
      }
    },
    handleClickOut(e) {
      if (!window.$.contains(this.$refs.selectBox, e.target)) {
        this.open = false;
      }
    },
    handleSearch(e) {
      if (this.$refs.options) {
        $(this.$refs.options)
          .children(".option")
          .each(function () {
            const text = (this.innerText || "").toLowerCase();
            if (text.indexOf(e.key) === 0) {
              this.scrollIntoView();
              // Stop looping
              return false;
            }
          });
      }
    },
    handleSelect(option) {
      this.selectedValue = option.value;
      this.open = false;
    },
    toggleOpen(e) {
      const focusedInput = e && e.target.className === "form-control";
      if (!this.disabled && !focusedInput) {
        this.open = !this.open;
      }
    },
    findByName
  }
};
</script>

<style lang="scss" scoped>
.hint-label {
  font-style: italic;
  color: #626262;
  white-space: pre !important;
}

.selected-value-hint {
  display: flex;
  flex-direction: row;
}

.selected-value-hint > .hint-label {
  color: #6c757d;
  font-size: 0.8rem;
  white-space: nowrap;
  width: 100%;
}
.no-wrap {
  white-space: nowrap;
}

.add-item-input {
  background-color: #ced4da;
  padding: 6px 10px;
  display: flex;
  :deep(input) {
    font-size: 13px !important;
    height: 30px;
  }
}
.add-item-button {
  background-color: var(--primary-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 5px 5px 0;
  margin-left: -4px;
  padding: 0 8px;
  height: 34px;
}

.admin-select-options {
  scroll-padding: 0;
}

.option-icon {
  margin-left: 5px;
}

.admin-select-options {
  z-index: 21; /* Just eclipsing the standard z-20 used for sticky footers on forms */
}
</style>
